/**
 * Missing Document Audit: 8-section checklist (PrepHERedness style).
 * Each item can be Physical, Digital, or Missing. Builds a prioritized task list from missing items.
 * Used by DocumentsTool; export to binder.
 */

export const STATUS_PHYSICAL = 'physical';
export const STATUS_DIGITAL = 'digital';
export const STATUS_MISSING = 'missing';

/**
 * Document audit sections (7). Quick-start items—contact sheets, grab list, utility shut-off—
 * are produced by other tools (Emergency Contacts, Emergency Plan); add those via Build your binder.
 */
export const DOCUMENT_AUDIT_SECTIONS = [
  {
    id: 'vital-id',
    label: 'Vital Identification',
    items: [
      { id: 'drivers-license', label: 'Driver’s license (copy)' },
      { id: 'passport', label: 'Passport (copy)' },
      { id: 'ssn-cards', label: 'Social Security cards (all family members)' },
      { id: 'birth-certificates', label: 'Birth certificates (all family members)' },
      { id: 'pet-id', label: 'Pet ID (photo with you, microchip number, vet records)' },
    ],
  },
  {
    id: 'medical',
    label: 'Medical Records',
    items: [
      { id: 'medical-snapshot', label: 'Medical snapshot (blood type, allergies, meds per person)' },
      { id: 'insurance-cards', label: 'Health / dental / vision insurance cards (front & back)' },
      { id: 'eyeglass-rx', label: 'Eyeglass or contact prescription' },
      { id: 'immunization', label: 'Immunization records' },
    ],
  },
  {
    id: 'home-property',
    label: 'Home & Property',
    items: [
      { id: 'deed-lease', label: 'Deed or lease agreement' },
      { id: 'mortgage', label: 'Recent mortgage statement' },
      { id: 'home-inventory', label: 'Home inventory (high-value items, serial numbers, photos)' },
      { id: 'vehicle-titles', label: 'Vehicle titles & registration' },
    ],
  },
  {
    id: 'financial',
    label: 'Financial Continuity',
    items: [
      { id: 'banking-list', label: 'Banking directory (accounts, routing, branch addresses)' },
      { id: 'credit-card-list', label: 'Credit card list + lost/stolen phone numbers' },
      { id: 'income-proof', label: 'Income proof (last 2 pay stubs or tax returns)' },
    ],
  },
  {
    id: 'insurance',
    label: 'Insurance Policies',
    items: [
      { id: 'policy-summaries', label: 'Policy summaries (home, auto, life, flood)' },
      { id: 'agent-contact', label: 'Insurance agent contact (name & number)' },
    ],
  },
  {
    id: 'legal',
    label: 'Legal Documents',
    items: [
      { id: 'will', label: 'Will or living trust' },
      { id: 'poa', label: 'Power of attorney (financial and healthcare)' },
      { id: 'dd214', label: 'Military discharge (DD-214), if applicable' },
    ],
  },
  {
    id: 'maps-intel',
    label: 'Maps & Local Intel',
    items: [
      { id: 'neighborhood-egress', label: 'Neighborhood egress (multiple routes out)' },
      { id: 'meeting-places', label: 'Meeting places (home, nearby, out of town)' },
      { id: 'safe-house', label: 'Safe house addresses (contact + directions)' },
    ],
  },
];

/**
 * Merge user-added custom items and custom sections into the built-in sections,
 * then apply the user's preferred ordering.
 * @param {{ [sectionId: string]: Array<{ id: string, label: string }> }} [customItems]
 * @param {Array<{ id: string, label: string }>} [customSections]
 * @param {string[]} [sectionOrder] — ordered array of section IDs
 * @param {{ [sectionId: string]: string[] }} [itemOrder] — ordered arrays of item IDs per section
 * @param {{ [sectionId: string]: string[] }} [hiddenItems] — item IDs to exclude per section
 * @returns {Array<{ id: string, label: string, items: Array<{ id: string, label: string }>, isCustomSection?: boolean }>}
 */
export function getMergedSections(customItems, customSections, sectionOrder, itemOrder, hiddenItems) {
  const items = customItems && typeof customItems === 'object' ? customItems : {};
  const iOrder = itemOrder && typeof itemOrder === 'object' ? itemOrder : {};
  const hidden = hiddenItems && typeof hiddenItems === 'object' ? hiddenItems : {};

  // Start with built-in sections, merging custom items and filtering hidden
  const sections = DOCUMENT_AUDIT_SECTIONS.map((section) => {
    const extras = items[section.id];
    let merged = extras && extras.length > 0 ? [...section.items, ...extras] : [...section.items];
    const hiddenSet = hidden[section.id];
    if (hiddenSet && hiddenSet.length > 0) {
      const hideIds = new Set(hiddenSet);
      merged = merged.filter((i) => !hideIds.has(i.id));
    }
    return { ...section, items: applyItemOrder(merged, iOrder[section.id]) };
  });

  // Append custom sections
  if (Array.isArray(customSections)) {
    customSections.forEach((cs) => {
      let sectionItems = items[cs.id] || [];
      const hiddenSet = hidden[cs.id];
      if (hiddenSet && hiddenSet.length > 0) {
        const hideIds = new Set(hiddenSet);
        sectionItems = sectionItems.filter((i) => !hideIds.has(i.id));
      }
      sections.push({
        id: cs.id,
        label: cs.label,
        items: applyItemOrder(sectionItems, iOrder[cs.id]),
        isCustomSection: true,
      });
    });
  }

  // Apply section ordering
  if (Array.isArray(sectionOrder) && sectionOrder.length > 0) {
    const byId = new Map(sections.map((s) => [s.id, s]));
    const ordered = [];
    sectionOrder.forEach((id) => {
      const s = byId.get(id);
      if (s) {
        ordered.push(s);
        byId.delete(id);
      }
    });
    // Append any sections not in the order array (newly added)
    byId.forEach((s) => ordered.push(s));
    return ordered;
  }

  return sections;
}

/** Reorder an items array according to an ordered ID list. */
function applyItemOrder(items, order) {
  if (!Array.isArray(order) || order.length === 0) return items;
  const byId = new Map(items.map((i) => [i.id, i]));
  const ordered = [];
  order.forEach((id) => {
    const item = byId.get(id);
    if (item) {
      ordered.push(item);
      byId.delete(id);
    }
  });
  byId.forEach((item) => ordered.push(item));
  return ordered;
}

/**
 * Default audit state: every item is "missing".
 * @param {{ [sectionId: string]: Array<{ id: string, label: string }> }} [customItems]
 * @param {Array<{ id: string, label: string }>} [customSections]
 * @returns {{ [sectionId: string]: { [itemId: string]: string } }}
 */
export function getDefaultAuditState(customItems, customSections) {
  const sections = getMergedSections(customItems, customSections);
  const state = {};
  sections.forEach((section) => {
    state[section.id] = {};
    section.items.forEach((item) => {
      state[section.id][item.id] = STATUS_MISSING;
    });
  });
  return state;
}

/**
 * Build prioritized task list from audit state (only missing items, ordered by section).
 * @param {{ [sectionId: string]: { [itemId: string]: string } }} auditState
 * @param {{ [sectionId: string]: Array<{ id: string, label: string }> }} [customItems]
 * @param {Array<{ id: string, label: string }>} [customSections]
 * @param {string[]} [sectionOrder]
 * @param {{ [sectionId: string]: string[] }} [itemOrder]
 * @param {{ [sectionId: string]: string[] }} [hiddenItems]
 * @returns {Array<{ sectionId: string, itemId: string, sectionLabel: string, itemLabel: string }>}
 */
export function buildTaskListFromAudit(auditState, customItems, customSections, sectionOrder, itemOrder, hiddenItems) {
  const sections = getMergedSections(customItems, customSections, sectionOrder, itemOrder, hiddenItems);
  const tasks = [];
  sections.forEach((section) => {
    section.items.forEach((item) => {
      if (auditState[section.id] && auditState[section.id][item.id] === STATUS_MISSING) {
        tasks.push({
          sectionId: section.id,
          itemId: item.id,
          sectionLabel: section.label,
          itemLabel: item.label,
        });
      }
    });
  });
  return tasks;
}

/**
 * Build binder-ready HTML sections from audit state (Document Audit + Uploaded Documents).
 * @param {{ [sectionId: string]: { [itemId: string]: string } }} auditState
 * @param {{ [sectionId: string]: { [itemId: string]: { fileName: string, mimeType: string, data: string, uploadedAt: string } } }} [uploads]
 * @param {{ [sectionId: string]: Array<{ id: string, label: string }> }} [customItems]
 * @param {Array<{ id: string, label: string }>} [customSections]
 * @param {string[]} [sectionOrder]
 * @param {{ [sectionId: string]: string[] }} [itemOrder]
 * @param {{ [sectionId: string]: string[] }} [hiddenItems]
 * @returns {Array<{ name: string, content: string }>}
 */
export function exportAuditToBinderSections(auditState, uploads, customItems, customSections, sectionOrder, itemOrder, hiddenItems) {
  const merged = getMergedSections(customItems, customSections, sectionOrder, itemOrder, hiddenItems);
  const binderSections = [];

  let auditHtml = '<div class="section-content">';
  merged.forEach((section) => {
    auditHtml += `<div class="section-title">${escapeHtml(section.label)}</div>`;
    section.items.forEach((item) => {
      const status = auditState[section.id] && auditState[section.id][item.id];
      const statusLabel = status === STATUS_PHYSICAL ? 'Physical' : status === STATUS_DIGITAL ? 'Digital' : 'Missing';
      auditHtml += `<div class="item-row"><span class="label">${escapeHtml(item.label)}:</span> <span class="value">${escapeHtml(statusLabel)}</span></div>`;
    });
  });
  auditHtml += '</div>';
  binderSections.push({ name: 'Document Audit', content: auditHtml });

  // Uploaded Documents section (only if there are uploads)
  if (uploads && typeof uploads === 'object') {
    let hasAny = false;
    let uploadHtml = '<div class="section-content">';

    merged.forEach((section) => {
      const sectionUploads = uploads[section.id];
      if (!sectionUploads) return;
      const uploadedItems = section.items.filter((item) => sectionUploads[item.id]);
      if (uploadedItems.length === 0) return;

      if (!hasAny) hasAny = true;
      uploadHtml += `<div class="section-title">${escapeHtml(section.label)}</div>`;
      uploadedItems.forEach((item) => {
        const upload = sectionUploads[item.id];
        uploadHtml += `<div class="item-row"><strong>${escapeHtml(item.label)}</strong> — ${escapeHtml(upload.fileName)}</div>`;
        if (upload.mimeType && upload.mimeType.startsWith('image/')) {
          uploadHtml += `<div class="item-row"><img src="data:${upload.mimeType};base64,${upload.data}" alt="${escapeHtml(upload.fileName)}" style="max-width:100%;max-height:600px;" /></div>`;
        } else if (upload.mimeType === 'application/pdf') {
          uploadHtml += `<div class="item-row"><object data="data:application/pdf;base64,${upload.data}" type="application/pdf" width="100%" height="400px"><p>PDF: ${escapeHtml(upload.fileName)} (included in digital archive)</p></object></div>`;
        }
      });
    });

    uploadHtml += '</div>';
    if (hasAny) {
      binderSections.push({ name: 'Uploaded Documents', content: uploadHtml });
    }
  }

  return binderSections;
}

function escapeHtml(text) {
  if (text == null) return '';
  const s = String(text);
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
